<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import {
  UiIcon,
  UiIconButton,
  UiStandardButton,
  UiText,
} from "@shopexperts/shared";
import type { PageCollection } from "@shopexperts/shared";
import { usePageCollections } from "../composables/usePageCollections";

const router = useRouter();
const page = ref(1);

const { collections, pagination, isLoading, error } = usePageCollections({
  page,
  perPage: 15,
});

const totalCollections = computed(() => pagination.value?.total ?? collections.value.length);
const showingLabel = computed(() => {
  if (!collections.value.length) return "No collections to show";
  const total = totalCollections.value;
  const start = ((pagination.value?.page ?? page.value) - 1) * (pagination.value?.per_page ?? 15) + 1;
  const end = start + collections.value.length - 1;
  return `Showing ${start}-${end} of ${total} collections`;
});
const isEmpty = computed(() => !isLoading.value && !collections.value.length);
const tableColumns = "minmax(220px,1.3fr) minmax(260px,1.55fr) minmax(260px,1.45fr) minmax(80px,0.45fr) minmax(160px,0.9fr) 48px";

onMounted(() => {
  window.scrollTo({ top: 0 });
});

function openCreate() {
  void router.push({ name: "admin-page-collections-create" });
}

function openEdit(collection: PageCollection) {
  void router.push({ name: "admin-page-collections-edit", params: { collectionId: collection.id } });
}

function nextPage() {
  if (pagination.value?.has_next) page.value += 1;
}

function previousPage() {
  if (pagination.value?.has_prev && page.value > 1) page.value -= 1;
}

function collectionDescription(collection: PageCollection) {
  return collection.description || "-";
}

function pagesCount(collection: PageCollection) {
  return collection.pages_count ?? 0;
}

function formatDate(value: string) {
  try {
    return new Intl.DateTimeFormat("en", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(value));
  } catch {
    return value;
  }
}
</script>

<template>
  <main class="min-w-0 flex-1 bg-white px-6 py-8 md:px-10 lg:px-16">
    <div class="mx-auto flex w-full max-w-[1500px] flex-col gap-8">
      <header class="page-collections-header">
        <div class="min-w-0">
          <nav class="mb-5 flex items-center gap-2 text-sm text-se-gray-700">
            <span>Page Collections</span>
            <span>/</span>
            <span class="font-semibold text-se-dark">Index</span>
          </nav>
          <UiText as="h1" variant="h1" class="page-collections-title" text="**Page Collections**" />
          <UiText
            as="p"
            variant="p"
            class="mt-3 max-w-3xl text-se-gray-700"
            text="Manage collections of pages and the metadata used to automatically group matching pages."
          />
        </div>
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
          <UiStandardButton label="Create Collection" class="page-collections-primary-button" @click="openCreate" />
        </div>
      </header>

      <section class="flex flex-col gap-4">
        <UiText as="p" variant="p" class="text-se-gray-700" :text="`${totalCollections} collections`" />

        <div class="page-collections-table overflow-x-auto rounded-lg border border-se-gray-300 bg-white">
          <div class="grid min-w-[980px] items-center gap-6 border-b border-se-gray-200 px-6 py-4 text-xs font-semibold uppercase tracking-[0.08em] text-se-gray-700" :style="{ gridTemplateColumns: tableColumns }">
            <span>Collection</span>
            <span>Description</span>
            <span>Slug</span>
            <span>Pages</span>
            <span>Updated</span>
            <span></span>
          </div>

          <button
            v-for="collection in collections"
            :key="collection.id"
            type="button"
            class="grid min-w-[980px] w-full items-center gap-6 border-b border-se-gray-200 px-6 py-5 text-left transition-colors last:border-b-0 hover:bg-se-gray-100"
            :style="{ gridTemplateColumns: tableColumns }"
            @click="openEdit(collection)"
          >
            <span class="min-w-0">
              <span class="block font-semibold text-se-dark">{{ collection.title }}</span>
            </span>
            <span class="text-sm text-se-gray-700">{{ collectionDescription(collection) }}</span>
            <span class="text-sm text-se-gray-700">{{ collection.slug }}</span>
            <span class="text-sm font-medium text-se-dark">{{ pagesCount(collection) }}</span>
            <span class="text-sm text-se-gray-700">
              <span class="block">{{ formatDate(collection.updated_at) }}</span>
              <span v-if="collection.last_editor" class="block">by {{ collection.last_editor.name }}</span>
            </span>
            <span class="justify-self-end">
              <UiIcon name="pencil" class="h-4 w-4 text-se-dark" />
            </span>
          </button>

          <div v-if="isLoading" class="px-6 py-14 text-center text-se-gray-700">Loading collections...</div>
          <div v-else-if="error" class="px-6 py-14 text-center text-red-600">Failed to load collections.</div>
          <div v-else-if="isEmpty" class="px-6 py-14 text-center text-se-gray-700">No page collections found.</div>
        </div>

        <footer class="flex flex-col gap-3 text-sm text-se-gray-700 sm:flex-row sm:items-center sm:justify-between">
          <span>{{ showingLabel }}</span>
          <div class="flex items-center gap-2">
            <UiIconButton icon="arrow-narrow-left" aria-label="Previous page" variant="outline" :disabled="!pagination?.has_prev" @click="previousPage" />
            <span class="min-w-10 text-center">{{ pagination?.page ?? page }}</span>
            <UiIconButton icon="arrow-right" aria-label="Next page" variant="outline" :disabled="!pagination?.has_next" @click="nextPage" />
          </div>
        </footer>
      </section>
    </div>
  </main>
</template>

<style scoped>
.page-collections-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
}

.page-collections-title {
  color: #08275a;
  letter-spacing: 0;
}

.page-collections-primary-button {
  background: linear-gradient(180deg, #193255 0%, #0b1b35 100%);
  color: white;
  box-shadow: 0 10px 20px rgb(8 25 57 / 14%);
}

.page-collections-table {
  border-color: #d9e0ec;
  border-radius: 14px;
}

@media (max-width: 900px) {
  .page-collections-header {
    align-items: flex-start;
    flex-direction: column;
  }

}
</style>
